import { useUser } from "@clerk/expo";
import { getLocales, type Locale } from "expo-localization";
import { I18n } from "i18n-js";
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Platform } from "react-native";

import enUS from "@/locales/en-US.json";
import esES from "@/locales/es-ES.json";
import frFR from "@/locales/fr-FR.json";

const defaultLocale: Locale = {
  currencyCode: "USD",
  currencySymbol: "$",
  decimalSeparator: ".",
  digitGroupingSeparator: ",",
  languageCode: "en",
  languageCurrencyCode: "USD",
  languageCurrencySymbol: "$",
  languageRegionCode: "US",
  languageScriptCode: "Latn",
  languageTag: "en-US",
  measurementSystem: "us",
  regionCode: "US",
  temperatureUnit: "fahrenheit",
  textDirection: "ltr",
};

const getBrowserLocale = (): Locale => {
  const locales = getLocales();
  return locales[0] ?? defaultLocale;
};

// Static export for use outside LocalizationProvider (e.g., _layout.tsx)
export const currentLocale = getBrowserLocale();

const localesMapping = {
  "en-US": enUS,
  "es-ES": esES,
  "fr-FR": frFR,
};

const createI18n = (locale: string): I18n => {
  const instance = new I18n(localesMapping);
  instance.locale = locale;
  instance.enableFallback = true;
  instance.defaultLocale = defaultLocale.languageTag;
  return instance;
};

export const initialI18n = createI18n(getBrowserLocale().languageTag);

export type LanguageOption = "browser" | keyof typeof localesMapping;

interface LocalizationContextType {
  browserLocale: Locale;
  i18n: I18n;
  locale: string;
  setStudyLanguage: (language: LanguageOption) => Promise<void>;
  setWebsiteLanguage: (language: LanguageOption) => Promise<void>;
  studyLanguage: LanguageOption;
  updatingStudyLanguage: boolean;
  updatingWebsiteLanguage: boolean;
  websiteLanguage: LanguageOption;
}

const LocalizationContext = createContext<LocalizationContextType>({
  browserLocale: currentLocale,
  i18n: initialI18n,
  locale: initialI18n.locale,
  setStudyLanguage: async () => {},
  setWebsiteLanguage: async () => {},
  studyLanguage: "browser",
  updatingStudyLanguage: false,
  updatingWebsiteLanguage: false,
  websiteLanguage: "browser",
});

export const LocalizationProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useUser();
  const [browserLocale, setBrowserLocale] = useState(getBrowserLocale);
  const [updatingWebsiteLanguage, setUpdatingWebsiteLanguage] = useState(false);
  const [updatingStudyLanguage, setUpdatingStudyLanguage] = useState(false);

  const websiteLanguage: LanguageOption =
    (user?.unsafeMetadata.websiteLanguage as LanguageOption) || "browser";
  const studyLanguage: LanguageOption =
    (user?.unsafeMetadata.studyLanguage as LanguageOption) || "browser";

  const resolvedLocale =
    websiteLanguage === "browser" ? browserLocale.languageTag : websiteLanguage;

  const i18n = useMemo(() => createI18n(resolvedLocale), [resolvedLocale]);

  useEffect(() => {
    if (Platform.OS !== "web") return;

    const handleLanguageChange = () => {
      setBrowserLocale(getBrowserLocale());
    };

    window.addEventListener("languagechange", handleLanguageChange);
    return () =>
      window.removeEventListener("languagechange", handleLanguageChange);
  }, []);

  const setWebsiteLanguage = useCallback(
    async (language: LanguageOption) => {
      if (!user) return;

      setUpdatingWebsiteLanguage(true);
      try {
        const newMetadata = { ...user.unsafeMetadata };

        if (language === "browser") {
          delete newMetadata.websiteLanguage;
        } else {
          newMetadata.websiteLanguage = language;
        }

        await user.update({ unsafeMetadata: newMetadata });
      } finally {
        setUpdatingWebsiteLanguage(false);
      }
    },
    [user]
  );

  const setStudyLanguage = useCallback(
    async (language: LanguageOption) => {
      if (!user) return;

      setUpdatingStudyLanguage(true);
      try {
        const newMetadata = { ...user.unsafeMetadata };

        if (language === "browser") {
          delete newMetadata.studyLanguage;
        } else {
          newMetadata.studyLanguage = language;
        }

        await user.update({ unsafeMetadata: newMetadata });
      } finally {
        setUpdatingStudyLanguage(false);
      }
    },
    [user]
  );

  const value = useMemo(
    () => ({
      browserLocale,
      i18n,
      locale: i18n.locale,
      setStudyLanguage,
      setWebsiteLanguage,
      studyLanguage,
      updatingStudyLanguage,
      updatingWebsiteLanguage,
      websiteLanguage,
    }),
    [
      browserLocale,
      i18n,
      setStudyLanguage,
      setWebsiteLanguage,
      studyLanguage,
      updatingStudyLanguage,
      updatingWebsiteLanguage,
      websiteLanguage,
    ]
  );

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error(
      "useLocalization must be used within a LocalizationProvider"
    );
  }
  return context;
};
